import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { exportMenu } from '@/lib/services/menuExport';
import type { ExportProgress } from '@/lib/services/menuExport';
import type { Restaurant } from '@/types';
import { Download, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

interface MenuExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurant: Restaurant;
}

/**
 * MenuExportModal - Handle menu export with progress tracking
 * Follows the app's design patterns and theme
 */
export const MenuExportModal: React.FC<MenuExportModalProps> = ({
  isOpen,
  onClose,
  restaurant
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    setExportError(null);
    setExportSuccess(false);
    setProgress(null);

    try {
      await exportMenu(restaurant, (progressData) => {
        setProgress(progressData);
      });
      
      setExportSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
      
    } catch (error) {
      console.error('Export failed:', error);
      setExportError(error instanceof Error ? error.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    if (!isExporting) {
      setProgress(null);
      setExportError(null);
      setExportSuccess(false);
      onClose();
    }
  };

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case 'collecting':
      case 'downloading_images':
      case 'creating_zip':
        return <Loader2 className="w-5 h-5 animate-spin text-primary" />;
      case 'complete':
        return <CheckCircle2 className="w-5 h-5 text-apple-green" />;
      default:
        return <Loader2 className="w-5 h-5 animate-spin text-gray-400" />;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Export Menu"
      size="md"
      closeOnOverlayClick={!isExporting}
      closeOnEscape={!isExporting}
    >
      <div className="space-y-6">
        {/* Restaurant Info */}
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {restaurant.name}
          </h3>
          <p className="text-sm text-gray-600">
            Export complete menu including digital menu items, image menu, and all associated images.
          </p>
        </div>

        {/* Export Content */}
        <AnimatePresence mode="wait">
          {/* Initial State - Ready to Export */}
          {!isExporting && !progress && !exportError && !exportSuccess && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div className="bg-primary bg-opacity-10 border border-primary border-opacity-20 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-primary mb-3">
                  What will be exported:
                </h4>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                    <span>Restaurant details (name, description, logo, theme, contact info)</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                    <span>Digital menu sections and items</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                    <span>Image menu photos and thumbnails</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                    <span>All images, settings and configurations</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          )}

          {/* Export in Progress */}
          {(isExporting || progress) && !exportError && !exportSuccess && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              {/* Progress Bar */}
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 font-medium">
                    {progress?.message || 'Starting export...'}
                  </span>
                  <span className="font-semibold text-gray-900">
                    {Math.round(progress?.progress || 0)}%
                  </span>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <motion.div
                    className="bg-primary h-2 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress?.progress || 0}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                
                {progress?.currentItem && (
                  <p className="text-xs text-gray-500 truncate">
                    {progress.currentItem}
                  </p>
                )}
              </div>

              {/* Stage Status */}
              {progress && (
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                  {getStageIcon(progress.stage)}
                  <span className="text-sm font-medium text-gray-700">
                    {progress.message}
                  </span>
                </div>
              )}
            </motion.div>
          )}

          {/* Export Success */}
          {exportSuccess && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-center space-y-4"
            >
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-apple-green bg-opacity-10 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-apple-green" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Export Completed!
                </h3>
                <p className="text-sm text-gray-600">
                  Your menu has been exported and downloaded successfully.
                </p>
              </div>
            </motion.div>
          )}

          {/* Export Error */}
          {exportError && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div className="flex items-start space-x-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                <AlertCircle className="w-5 h-5 text-apple-red mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-semibold text-red-900 mb-1">
                    Export Failed
                  </h4>
                  <p className="text-sm text-red-800">
                    {exportError}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={isExporting}
            className="flex-1"
          >
            {exportSuccess ? 'Close' : 'Cancel'}
          </Button>
          
          {!exportSuccess && !exportError && (
            <Button
              variant="primary"
              leftIcon={<Download className="w-4 h-4" />}
              onClick={handleExport}
              disabled={isExporting}
              className="flex-1"
            >
              {isExporting ? 'Exporting...' : 'Start Export'}
            </Button>
          )}
          
          {exportError && (
            <Button
              variant="primary"
              leftIcon={<Download className="w-4 h-4" />}
              onClick={handleExport}
              className="flex-1"
            >
              Try Again
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
